/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import {
  type Boundary,
  FloatingFocusManager,
  FloatingPortal,
  safePolygon,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import { arrow, autoUpdate, flip, hide, limitShift, offset, shift } from '@floating-ui/react-dom';
import type { DetectOverflowOptions, ReferenceType } from '@floating-ui/react-dom';

import logger from 'js/app/loggerSingleton';
import { isRightToLeft } from 'js/lib/language';

import { MuiFade, useFocusRing } from '@coursera/cds-core';

import Tooltip from 'bundles/common/components/Tooltip/Tooltip';
import TooltipArrow from 'bundles/common/components/Tooltip/TooltipArrow';
import { ARROW_HEIGHT, TRANSITION_MS } from 'bundles/common/components/Tooltip/constants';
import type { Placement, Variant } from 'bundles/common/components/Tooltip/types';
import { getOnlyChild, useDismissOnResize } from 'bundles/common/components/Tooltip/utils';
import generateUUID from 'bundles/common/utils/uuid';

import _t from 'i18n!nls/common';

export type Props = {
  message: React.ReactNode;

  id?: string;
  className?: string;
  placement?: Placement;
  delayShow?: number;
  delayHide?: number;
  variant?: Variant;
  trigger?: 'hover' | 'click' | 'controlled';
  show?: boolean;
  offset?: number;
  enableArrow?: boolean;
  boundaryElement?: Boundary;
  boundaryElementMargin?: number;
  /**
   * Hides the tooltip when the reference element is hidden
   * see https://floating-ui.com/docs/hide#hide
   *
   * @default false
   */
  autoHide?: boolean;

  /**
   * A callback invoked when both the reference and floating elements are
   * mounted, and cleaned up when either is unmounted. This is useful for
   * setting up event listeners (e.g. pass `autoUpdate`).
   * see https://floating-ui.com/docs/usefloating#whileelementsmounted
   */
  whileElementsMounted?: (reference: ReferenceType, floating: HTMLElement, update: () => void) => () => void;

  style?: React.CSSProperties;
  'data-testid'?: string;
};

const FloatingTooltip = ({
  placement = 'top',
  children,
  delayShow = 50,
  delayHide,
  message,
  className,
  id,
  variant,
  trigger = 'hover',
  show = true,
  offset: tooltipOffset = 0,
  whileElementsMounted = autoUpdate,
  autoHide = false,
  enableArrow,
  boundaryElement,
  boundaryElementMargin = 0,
  style = {},
  'data-testid': dataTestId,
}: React.PropsWithChildren<Props>) => {
  const [open, setOpen] = useState(false);
  const [tooltipId, setTooltipId] = useState(id);

  const arrowRef = useRef<SVGSVGElement>(null);

  const shouldOpen = trigger === 'controlled' ? show : open;

  useEffect(() => {
    setTooltipId(id || generateUUID());
  }, [id]);

  const overflowOptions: DetectOverflowOptions = {
    boundary: boundaryElement,
    padding: boundaryElement ? boundaryElementMargin : undefined,
  };

  // defines methods for positioning the tooltip
  const middleware = [
    offset(tooltipOffset + ARROW_HEIGHT), // sets pixel offset relative to reference element
    shift({ limiter: limitShift(), ...overflowOptions }), // shifts the tooltip along the main axis to keep it in view
    flip({ ...overflowOptions }), // changes the placement of the tooltip to the opposite side to keep it in view
    arrow({ element: arrowRef, padding: 4 }), // provides positioning data to center the tooltip arrow
    autoHide ? hide({ strategy: 'referenceHidden', ...overflowOptions }) : null, // hides the tooltip if the reference element is hidden
    autoHide ? hide({ strategy: 'escaped', ...overflowOptions }) : null, // hides the tooltip if the tooltip escapes the boundary
  ];

  const hover = trigger === 'hover';
  const popover = trigger === 'click';

  const {
    context,
    x: floatingX,
    y: floatingY,
    refs: { setReference, setFloating },
    middlewareData: { hide: { referenceHidden = false, escaped = false } = {} },
    placement: actualPlacement,
  } = useFloating({
    placement,
    strategy: 'fixed',
    open,
    onOpenChange: setOpen,
    middleware,
    whileElementsMounted,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, {
      enabled: hover,
      handleClose: safePolygon(),
      delay: { open: delayShow, close: delayHide },
    }),
    useFocus(context, { enabled: hover, visibleOnly: false }),
    useClick(context, { enabled: popover }),
    useDismiss(context, { referencePress: hover, ancestorScroll: true, escapeKey: true }),
    useDismissOnResize(context, { enabled: hover || popover }),
    useRole(context, { role: hover ? 'tooltip' : 'dialog' }),
  ]);

  const { isFocusVisible, focusProps } = useFocusRing();

  const refElement = getOnlyChild(children);

  // @ts-expect-error
  const originalRef = refElement?.ref;
  const mergedRefs = useMergeRefs([setReference, originalRef]);

  if (!refElement) {
    logger.error('Error: FloatingTooltip called without a valid child element');
    return null;
  }

  const showFloatingElement = show && shouldOpen;

  // attaches floating-ui props to the reference element without overriding
  // event listeners like click handlers, ref, etc...
  const refProps = getReferenceProps({
    ref: mergedRefs,
    'aria-describedby': showFloatingElement && hover ? tooltipId : undefined,
    onKeyDown(e) {
      if (e.key === 'Enter') setOpen(true);
    },
    ...refElement.props,
  });
  const floatingProps = getFloatingProps({ ref: setFloating, id: tooltipId, ...(popover ? focusProps : {}) });

  const horizontalDir = isRightToLeft(_t.getLocale()) ? 'right' : 'left';
  const tooltipPlacement = actualPlacement.split('-')[0] as Placement;

  const tooltipProps = {
    ...floatingProps,
    className,
    variant,
    focusVisible: isFocusVisible,
    style: {
      position: 'fixed',
      [horizontalDir]: floatingX ?? '',
      top: floatingY ?? '',
      visibility: referenceHidden || escaped ? 'hidden' : 'visible',
      ...style,
    } as const,
    enableArrow,
    placement: tooltipPlacement,
    arrow: <TooltipArrow ref={arrowRef} context={context} placement={tooltipPlacement} />,
    'data-testid': dataTestId,
  };

  const tooltip = <Tooltip {...tooltipProps}>{message}</Tooltip>;

  const floatingElement = popover ? (
    <FloatingFocusManager context={context} modal={false} order={['floating', 'content']}>
      {tooltip}
    </FloatingFocusManager>
  ) : (
    tooltip
  );

  return (
    <React.Fragment>
      {React.cloneElement(refElement, refProps)}
      <FloatingPortal id="floating-tooltip-portal">
        <MuiFade in={showFloatingElement} timeout={TRANSITION_MS} mountOnEnter unmountOnExit>
          {floatingElement}
        </MuiFade>
      </FloatingPortal>
    </React.Fragment>
  );
};

export default FloatingTooltip;
